import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/api";
import { BookOpen, Loader2 } from "lucide-react";
import { toast } from "sonner";

const SIDE = "https://images.unsplash.com/photo-1509021436665-8f07dbf5bf1d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user && user !== true) {
      if (user.role === "admin" || user.status === "approved") navigate("/dashboard");
      else navigate("/pending");
    }
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const u = await login(email, password);
      toast.success("Grace and peace — welcome back");
      if (u.role === "admin" || u.status === "approved") navigate("/dashboard");
      else navigate("/pending");
    } catch (e2) {
      setError(apiError(e2));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FAF7EE]">
      <div className="hidden lg:block relative">
        <img src={SIDE} alt="An open Bible in warm light" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#3D5844]/35" />
        <div className="absolute bottom-12 left-12 right-12">
          <p className="font-heading text-4xl text-[#FAF7EE] leading-snug">Welcome home, beloved.</p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <BookOpen className="w-6 h-6 text-[#C46B4E]" strokeWidth={1.6} />
            <span className="font-heading text-2xl">The Peace Plot</span>
          </Link>
          <h1 className="font-heading text-4xl text-[#2A2621]">Sign in</h1>
          <p className="text-[#615A50] mt-1">Return to your devotionals, prayers, and the Word.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="text-sm font-medium text-[#615A50]">Email</label>
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                className="mt-1.5 w-full rounded-xl border border-[#D5CEBF] bg-white px-4 py-3 text-[#2A2621] outline-none focus:ring-2 focus:ring-[#3D5844]/40"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-[#615A50]">Password</label>
                <Link to="/forgot-password" data-testid="login-forgot-password" className="text-xs text-[#C46B4E] font-medium hover:underline">Forgot password?</Link>
              </div>
              <input
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                className="mt-1.5 w-full rounded-xl border border-[#D5CEBF] bg-white px-4 py-3 text-[#2A2621] outline-none focus:ring-2 focus:ring-[#3D5844]/40"
                placeholder="••••••••"
              />
            </div>
            {error && <p className="text-sm text-[#AC5B40]" data-testid="login-error">{error}</p>}
            <button
              type="submit" disabled={loading} data-testid="login-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#3D5844] text-white py-3 font-medium hover:bg-[#2F4535] transition-colors disabled:opacity-60"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Sign in
            </button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-[#D5CEBF]" />
            <span className="text-xs text-[#615A50]">or</span>
            <div className="h-px flex-1 bg-[#D5CEBF]" />
          </div>

          <button
            onClick={handleGoogle} data-testid="login-google"
            className="w-full flex items-center justify-center gap-3 rounded-xl border border-[#D5CEBF] bg-white py-3 font-medium text-[#2A2621] hover:bg-[#F3EFE4] transition-colors"
          >
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
            Continue with Google
          </button>

          <p className="mt-6 text-sm text-[#615A50] text-center">
            New here?{" "}
            <Link to="/register" data-testid="login-to-register" className="text-[#C46B4E] font-medium hover:underline">Request access</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
